import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import SearchPagination from '../Search/components/SearchPagination';
import PortalListingSearchResultsContainer from './components/PortalListingSearchResultsContainer';
import { useSearchConfig } from '../Search/hooks/useSearchConfig';
import { usePortalListing } from './hooks/usePortalListing';
import BaseListing from '../BaseListing/BaseListing';
import SearchResultsCount from '../Search/components/SearchResultsCount';
import PortalFilters from './components/PortalFilters';
import { columnData } from './data/PortalListingColumnData';
import { SearchFacet, SearchNoResultsVariant, TableColumn } from '../../types/search';
import ActiveSearchTerm from '../Search/components/ActiveSearchTerm';
import PortalAppliedFilters from './components/PortalAppliedFilters';
import { PortalDateFilterProvider, useDateFilter } from './contexts/PortalDateFilterProvider';
import CalendarTopControls from './components/CalendarView/CalendarTopControls';
import CalendarCardStack from './components/CalendarView/CalendarCardStack';
import StatusQuickFilters from './components/StatusQuickFilters';
import { CALENDAR_DATE_FACET } from './index';
import { DateFormatter, getLocalTimeZone, parseDate } from '@internationalized/date';
import clsx from 'clsx';

const parsePrefilterCategoryIds = (rawData: string | undefined): string[] => {
  if (!rawData) return [];
  try {
    const parsed = JSON.parse(rawData);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

/**
 * Helper component to sync the date filter context with appliedFilters
 */
function DateFilterInitializer({ appliedFilters }: { appliedFilters: SearchFacet[] }): null {
  const calendarDateFilter = useDateFilter(CALENDAR_DATE_FACET);
  const lastSyncedValue = React.useRef<string>('');

  React.useEffect(() => {
    if (!calendarDateFilter || !CALENDAR_DATE_FACET) {
      return;
    }

    // Find the calendar date facet in appliedFilters
    const dateFacet = appliedFilters.find(f => f.key === CALENDAR_DATE_FACET);
    if (!dateFacet) {
      return;
    }

    // Extract date values from facet items
    if (dateFacet.items && dateFacet.items.length >= 2) {
      const dateValues = dateFacet.items.map(item => item.value);
      const valueToStore = dateValues.join(',');

      // Only update if the value has changed to avoid infinite loops
      if (valueToStore === lastSyncedValue.current) {
        return;
      }

      // Format label
      try {
        const formatter = new DateFormatter('default', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
        const startDate = parseDate(dateValues[0]);
        const endDate = parseDate(dateValues[1]);
        const startFormatted = formatter.format(startDate.toDate(getLocalTimeZone()));
        const endFormatted = formatter.format(endDate.toDate(getLocalTimeZone()));
        const label = `${startFormatted} - ${endFormatted}`;

        calendarDateFilter.setSelectedValue(valueToStore);
        calendarDateFilter.setSelectedValueLabel(label);
        lastSyncedValue.current = valueToStore;
      } catch (e) {
        console.error('Error formatting date label:', e);
      }
    }
  }, [appliedFilters, calendarDateFilter]);

  return null;
}

const PortalListingInner = () => {
  const config = useSearchConfig();
  const mount = document.getElementById('portal-listing-root');

  // Consolidate mount dataset parsing
  const mountDataset = React.useMemo(() => {
    if (!mount)
      return { listingType: 'documents', useQuickFilters: false, columnLabels: undefined, prefilterCategoryIds: [] };

    return {
      listingType: mount.dataset.listingType ?? 'documents',
      useQuickFilters: mount.dataset.useQuickFilters === 'true',
      columnLabels: mount.dataset.columnLabels ? JSON.parse(mount.dataset.columnLabels) : undefined,
      prefilterCategoryIds: parsePrefilterCategoryIds(mount.dataset.prefilterDocumentCategories),
    };
  }, [mount]);

  const { listingType, useQuickFilters, columnLabels, prefilterCategoryIds } = mountDataset;
  const isCalendarView = listingType === 'jobs';

  const {
    rows = [],
    sortByOptions = [],
    isLoading,
    isFetching,
    isError,
    isSuccess,
    pagination,
    handlePageChange,
    handleSortChange,
    summary,
    facets,
    applyPendingFilters: applyPendingFiltersBase,
    appliedFilters,
    requestedFilters,
    hasFiltersApplied,
    clearAllFilters,
    removeFilter,
    search,
    calendarDateFacet,
  } = usePortalListing(
    config.siteSearchApi,
    config.paginationDisplayLimit,
    config.documentContentTypes,
    prefilterCategoryIds,
    'pdfcategoryids',
    listingType
  );

  const applyPendingFilters = React.useCallback(
    (pendingFilters: Record<string, string | string[]>) => {
      applyPendingFiltersBase(pendingFilters);
    },
    [applyPendingFiltersBase]
  );

  const columns = React.useMemo<TableColumn[]>(() => {
    const baseColumns = columnData[listingType];
    if (!baseColumns) return [];

    if (!columnLabels) return baseColumns;

    const convertedLabels: { [key: string]: string } = {};
    for (const key of Object.keys(columnLabels)) {
      const camelCaseKey = key.charAt(0).toLowerCase() + key.slice(1);
      convertedLabels[camelCaseKey] = columnLabels[key];
    }

    return baseColumns.map(col => ({
      ...col,
      label: convertedLabels[col.key] ?? col.label,
    }));
  }, [listingType, columnLabels]);

  const totalResults = React.useMemo(() => {
    if (summary && typeof summary.total === 'number') {
      return summary.total;
    }
    return rows.length;
  }, [rows.length, summary]);

  const startResult = React.useMemo(() => {
    if (totalResults === 0) return 0;
    if (!pagination?.currentPage || pagination.currentPage < 1) return 1;

    return (pagination.currentPage - 1) * config.paginationDisplayLimit + 1;
  }, [pagination?.currentPage, config.paginationDisplayLimit, totalResults]);

  const endResult = React.useMemo(() => {
    if (!pagination?.currentPage || pagination.currentPage < 1)
      return Math.min(totalResults ?? 0, config.paginationDisplayLimit);

    const calculatedEnd = pagination.currentPage * config.paginationDisplayLimit;

    return Math.min(calculatedEnd, totalResults ?? calculatedEnd);
  }, [pagination?.currentPage, config.paginationDisplayLimit, totalResults]);

  const [sortOrder, setSortOrder] = React.useState<string | undefined>('1');
  const [sortCol, setSortCol] = React.useState<string | undefined>(undefined);
  const cachedStatusFacetRef = React.useRef<SearchFacet | null>(null);

  React.useEffect(() => {
    setSortOrder(summary?.sortOrder ?? '1');
    setSortCol(summary?.sortCol ?? undefined);
  }, [summary?.sortOrder, summary?.sortCol]);

  const handleSort = (newSortOrder: string, newSortCol: string) => {
    setSortOrder(newSortOrder);
    setSortCol(newSortCol);
    handleSortChange(newSortOrder, newSortCol);
  };

  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);

  React.useEffect(() => {
    setSelectedRows([]);
  }, [pagination?.currentPage, rows]);

  const resultsContainer = (
    <PortalListingSearchResultsContainer
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      noResultsText={config.labels.noResultsText}
      onSortChange={handleSort}
      sortOrder={sortOrder}
      sortCol={sortCol}
      listingType={listingType}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      isPaginationActive={!!(pagination && pagination.totalPages > 1)}
    />
  );

  const paginationNode =
    pagination && pagination.totalPages > 1 ? (
      <div className="search__pagination-container">
        <SearchPagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          prevPageLabel={config.labels.prevPageLabel}
          nextPageLabel={config.labels.nextPageLabel}
        />
      </div>
    ) : null;

  const showResults = React.useMemo(() => rows && rows.length > 0, [rows]);

  const hasSearchQuery = React.useMemo(() => search.query !== null && search.query !== '', [search.query]);

  // Determine the no-results variant based on the current state
  const noResultsVariant = React.useMemo<SearchNoResultsVariant>(() => {
    // If there's a search query, show the search no-results message (full screen replacement)
    if (hasSearchQuery) {
      return 'search';
    }
    // If non-date filters are applied, show the filter no-results variant
    // Note: hasFiltersApplied for calendar view already excludes dateFrom/dateTo
    if (hasFiltersApplied) {
      return 'filter';
    }
    // For calendar view (jobs) with no search query and no non-date filters,
    // it's a date-related no results (user selected a date with no jobs)
    if (isCalendarView) {
      return 'date';
    }
    // Default to search variant
    return 'search';
  }, [hasSearchQuery, isCalendarView, hasFiltersApplied]);

  // For 'date' and 'filter' no-results variants, we still want to show the controls
  // Only hide controls when it's a 'search' no-results (full screen replacement)
  const showControls = React.useMemo(
    () => showResults || noResultsVariant === 'date' || noResultsVariant === 'filter',
    [showResults, noResultsVariant]
  );

  const shouldShowActiveSearchTerm = React.useMemo(
    () => (!isCalendarView && hasSearchQuery) || (isCalendarView && !showResults && hasSearchQuery),
    [isCalendarView, hasSearchQuery, showResults]
  );

  const shouldShowSearchResultsCount = React.useMemo(
    () => showResults && !isCalendarView && (search.query === null || search.query === ''),
    [showResults, isCalendarView, search.query]
  );

  const statusFacet = React.useMemo(() => {
    // Clear cache and return null on API error
    if (isError) {
      cachedStatusFacetRef.current = null;
      return null;
    }

    // API returns facets even with empty results, so we should find status facet
    const found = facets.find((facet: SearchFacet) => facet.key.toLowerCase() === 'status');
    if (found) {
      cachedStatusFacetRef.current = found;
      return found;
    }

    // Fall back to cache only during loading transitions
    return cachedStatusFacetRef.current;
  }, [facets, isError]);

  // Determine if StatusQuickFilters will be shown - used to decide where PortalAppliedFilters renders
  const showStatusQuickFilters = showControls && useQuickFilters && (showResults || noResultsVariant !== 'date');

  return (
    <BaseListing
      columns={columns}
      isLoading={isLoading || isFetching}
      isError={isError}
      isSuccess={isSuccess}
      results={rows}
      listingType={listingType}
      noResultsVariant={noResultsVariant}
      filters={
        <PortalDateFilterProvider>
          <DateFilterInitializer appliedFilters={appliedFilters} />
          <div className={`portal__controls portal__controls--${listingType}`}>
            {showControls && !showStatusQuickFilters && (
              <div className="portal__controls-row portal__controls-row--top">
                <PortalAppliedFilters
                  appliedFilters={appliedFilters}
                  hasFiltersApplied={hasFiltersApplied}
                  removeFilter={removeFilter}
                  listingType={listingType}
                />
              </div>
            )}
            <div
              className={clsx('portal__controls-row portal__controls-row--middle', {
                'portal__controls-row--calendar': isCalendarView,
              })}
            >
              {showControls && isCalendarView ? (
                <CalendarTopControls applyPendingFilters={applyPendingFilters} calendarDateFacet={calendarDateFacet} />
              ) : null}
              {shouldShowActiveSearchTerm ? <ActiveSearchTerm searchTerm={search.query} /> : null}
              {shouldShowSearchResultsCount ? (
                <SearchResultsCount startResult={startResult} endResult={endResult} totalResults={totalResults} />
              ) : null}
              <PortalFilters
                facetGroups={facets}
                appliedFilters={appliedFilters}
                applyPendingFilters={applyPendingFilters}
                hasFiltersApplied={hasFiltersApplied}
                clearAllFilters={clearAllFilters}
                searchTerm={search.query}
                updateSearchTerm={search.updateQuery}
                sortByOptions={sortByOptions}
                onSortChange={handleSort}
                sortOrder={sortOrder}
                sortCol={sortCol}
                showBulkDownload={listingType === 'invoices'}
                selectedRows={selectedRows}
                isError={isError}
                isSuccess={isSuccess}
                resultsCount={totalResults}
                listenToDatepickerEvent={isCalendarView}
              />
            </div>
            {showControls && isCalendarView ? (
              <CalendarCardStack
                applyPendingFilters={applyPendingFilters}
                facetGroups={facets}
                appliedFilters={appliedFilters}
                calendarDateFacet={calendarDateFacet}
              />
            ) : null}
            {showStatusQuickFilters ? (
              <StatusQuickFilters
                statusFacet={statusFacet}
                appliedFilters={appliedFilters}
                requestedFilters={requestedFilters}
                applyPendingFilters={applyPendingFilters}
                removeFilter={removeFilter}
                calendarDateValue={calendarDateFacet?.selectedValue}
                searchTerm={search.query}
                isFetching={isFetching}
                hasFiltersApplied={hasFiltersApplied}
                listingType={listingType}
                totalResults={totalResults}
              />
            ) : null}
            {showControls && (
              <div>
                <div></div>
              </div>
            )}
          </div>
        </PortalDateFilterProvider>
      }
      pagination={paginationNode}
      resultsContainer={resultsContainer}
      clearAllFilters={clearAllFilters}
      updateSearchTerm={search.updateQuery}
    />
  );
};

const DocumentsListingQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

export { DocumentsListingQueryClient };

const PortalListing = () => {
  return (
    <QueryClientProvider client={DocumentsListingQueryClient}>
      <PortalListingInner />
    </QueryClientProvider>
  );
};

export default PortalListing;
